import Vue from 'vue';
import api from '../../api';

const state = {
  users: {},
  orgUsers: {},
};

const getters = {
  users(state) {
    return state.users;
  },
  // Array of all users in organisation, SuperAdmins filtered out if necessary
  allUsers(state, _, root, rootGetters) {
    return (rootGetters.app_user && rootGetters.app_user.role === 'SuperAdmin') ?
      Object.keys(state.users).map(id => state.users[id]) :
      Object.keys(state.users)
        .map(id => state.users[id])
        .filter(user => !(user.role === 'SuperAdmin'))
  },
  // Array of all users that can log in
  appUsers(_, getters) {
    return getters.allUsers.filter(user => !!user.cognitoId);
  },
  // Array of active users that can log in
  activeUsers(_, getters) {
    return getters.appUsers
      .filter(user => !user.disabled)
      .reduce((obj, curr) => {
        obj[curr.id] = curr;
        return obj;
      }, {})
  },
  orgUsers(state) {
    return state.orgUsers;
  },
};

const mutations = {
  setUsers(state, payload) {
    state.users = payload;
  },
  setUser(state, payload) {
    Vue.set(state.users, payload.id, payload);
  },
  setOrgUsers(state, payload) {
    state.orgUsers = payload;
  },
};

const actions = {
  setUsers(context, payload) {
    const users = {};
    payload.forEach(user => {
      user = prepUser(user);
      users[user.id] = user;
    });
    context.commit('setUsers', users);
  },
  setOrgUsers(context, payload) {
    const users = {};
    payload.forEach(user => {
      user = prepUser(user);
      users[user.id] = user;
    });
    context.commit('setOrgUsers', users);
  },
  getOrgUsers(context, org_id) {
    return new Promise(async (resolve, reject) => {
      try {
        const users = await api.getOrgUsers(org_id);
        context.dispatch('setOrgUsers', users.data.users.items || {});
        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  getUsers(context) {
    return new Promise(async (resolve, reject) => {
      try {
        const org_id = context.getters.org.id;
        const users = await api.getOrgUsers(org_id);
        context.dispatch('setUsers', users.data.users.items || {});
        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  getUser(context, id) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await api.getUser(id);

        if (res && res.data && res.data.user) {
          const user = prepUser(res.data.user);
          if (user) {
            context.commit('setUser', user);
          }
        }

        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  deleteUser(context, user) {
    return new Promise(async (resolve, reject) => {
      try {
        await api.deleteUser(user);
        context.dispatch('refreshUsers', user.organizationId);
        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  createUser(context, user) {
    return new Promise(async (resolve, reject) => {
      try {
        await api.createUser(user);
        context.dispatch('refreshUsers', user.organizationId);
        resolve();
      } catch (e) {
        if (
          e.response &&
          e.response.data &&
          e.response.data.details &&
          e.response.data.details.code === 'UsernameExistsException'
        ) reject(new Error('A user with that email already exists'))
        reject(e);
      }
    });
  },
  updateUser(context, user) {
    return new Promise(async (resolve, reject) => {
      try {
        await api.updateProfile(user);
        context.dispatch('refreshUsers', user.organizationId);
        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  async refreshUsers(context, orgId) {
    await context.dispatch('getUsers');
    context.dispatch('getOrgUsers', orgId);
  },
};

const getInitials = name => {
  if (typeof name !== 'string') {
    return '';
  }

  const nameArray = name.split(' ');
  return nameArray
    .map(n => n.charAt(0))
    .slice(0, 4)
    .join('')
    .toUpperCase();
};

const prepUser = user => {
  user.name = `${user.firstName} ${user.lastName}`;
  user.initials = getInitials(user.name);
  user.meta = user.jobTitle;
  user.userId = user.id;

  return user;
};

export default {
  state,
  getters,
  mutations,
  actions,
};
